import { ContentType, request } from "../api";
import { baseParams, getToken } from "../common";

/**
 * Заявки в поддержку (b2c-бэкенд, POST /api/support/...).
 *
 * Авторизация — одна из двух:
 *  - залогиненный клиент: обычный Bearer (как /order-list);
 *  - гость: X-Support-Token, выданный после подтверждения кода из письма (хранится в localStorage
 *    вместе с order_id и сроком). При 401 / ошибке 45 гостевой токен удаляется и показывается форма доступа.
 *
 * Все функции возвращают data ответа или бросают SupportError ({ code, key, status, details }) —
 * и для HTTP 4xx, и для ответа 200 с error_code.
 */

export const SUPPORT_GUEST_STORAGE_KEY = "support_guest";
// событие окна: гостевой доступ изменился (выдан / истёк / удалён) — его слушает SupportProvider
export const SUPPORT_GUEST_CHANGED_EVENT = "support-guest-changed";

export const SUPPORT_ACCESS_REQUIRED_ERROR = 45;

export class SupportError extends Error {
  constructor({ code = null, key = null, status = null, details = [], desc = "" } = {}) {
    super(desc || key || "support_error");
    this.code = code;
    this.key = key;
    this.status = status;
    this.details = Array.isArray(details) ? details : [];
  }
}

/** Гостевая сессия {token, order_id, expires_at} или null (просроченная удаляется). */
export const getSupportGuestSession = () => {
  try {
    const session = JSON.parse(localStorage.getItem(SUPPORT_GUEST_STORAGE_KEY) || "null");

    if (!session?.token) return null;

    if (session.expires_at && new Date(session.expires_at).getTime() <= Date.now()) {
      localStorage.removeItem(SUPPORT_GUEST_STORAGE_KEY);
      return null;
    }

    return session;
  } catch (error) {
    return null;
  }
};

const emitGuestChanged = () => window.dispatchEvent(new Event(SUPPORT_GUEST_CHANGED_EVENT));

export const setSupportGuestSession = session => {
  try {
    localStorage.setItem(SUPPORT_GUEST_STORAGE_KEY, JSON.stringify(session));
  } catch (error) {
    console.warn(error);
  }

  emitGuestChanged();
};

export const clearSupportGuestSession = () => {
  if (localStorage.getItem(SUPPORT_GUEST_STORAGE_KEY) === null) return;

  localStorage.removeItem(SUPPORT_GUEST_STORAGE_KEY);
  emitGuestChanged();
};

/** Заголовки авторизации заявок: Bearer клиента, иначе гостевой токен. */
export const getSupportAuthHeaders = () => {
  const token = getToken();

  if (token) return { Authorization: `Bearer ${token}` };

  const guest = getSupportGuestSession();

  return guest ? { "X-Support-Token": guest.token } : {};
};

const isGuestMode = () => !getToken() && !!getSupportGuestSession();

const readBlobJson = async blob => {
  try {
    return JSON.parse(await blob.text());
  } catch (error) {
    return null;
  }
};

const toSupportError = async (error, wasGuest) => {
  const status = error?.response?.status ?? null;
  let data = error?.response?.data;

  if (typeof Blob !== "undefined" && data instanceof Blob) data = await readBlobJson(data);

  const code = data?.error_code ?? null;

  // гостевой токен истёк / отозван — забываем его, страница покажет форму доступа
  if (wasGuest && (status === 401 || code === SUPPORT_ACCESS_REQUIRED_ERROR)) clearSupportGuestSession();

  return new SupportError({
    code: code ?? (status === 401 ? SUPPORT_ACCESS_REQUIRED_ERROR : null),
    key: data?.error_key ?? null,
    status,
    details: data?.details,
    desc: data?.error_desc || error?.message,
  });
};

const call = async (path, body = {}, { multipart = false, auth = true, format = "json" } = {}) => {
  const wasGuest = auth && isGuestMode();

  let response;

  try {
    response = await request({
      path: `/support/${path}`,
      method: "POST",
      body: { ...baseParams, ...body },
      type: multipart ? ContentType.FormData : ContentType.Json,
      format,
      headers: auth ? getSupportAuthHeaders() : {},
    });
  } catch (error) {
    throw await toSupportError(error, wasGuest);
  }

  const data = response?.data;

  if (format === "json" && data?.error_code) {
    throw await toSupportError({ response: { status: response.status, data } }, wasGuest);
  }

  // вместо файла пришёл JSON с ошибкой
  if (format === "blob" && typeof data?.type === "string" && data.type.includes("json")) {
    throw await toSupportError({ response: { status: response.status, data } }, wasGuest);
  }

  return data;
};

// ---- гостевой доступ (по номеру заказа и email брони) ----

/** → {status, email: "pa***@gmail.com", expires_in} */
export const supportGuestRequestCode = ({ order_id, email }) =>
  call("guest/request-code", { order_id, email }, { auth: false });

/** → {status, token, expires_at, order_id}; токен сразу сохраняется. */
export const supportGuestVerify = async ({ order_id, code }) => {
  const data = await call("guest/verify", { order_id, code }, { auth: false });

  if (data?.token) {
    setSupportGuestSession({ token: data.token, order_id: data.order_id || order_id, expires_at: data.expires_at || null });
  }

  return data;
};

// ---- заявки ----

export const supportMeta = () => call("meta");

/** params: {status?: 'open'|..., order_id?, unread?, page?, per_page?} → {data, meta} */
export const supportTickets = (params = {}) => call("tickets", params);

/** {subject, message, category, service_type?, order_id?, files?: File[]} → {status, ticket} */
export const supportTicketCreate = ({ files = [], ...fields }) => {
  const body = Object.fromEntries(Object.entries(fields).filter(([, value]) => value !== null && value !== undefined && value !== ""));

  if (files.length) body["files[]"] = files;

  return call("ticket-create", body, { multipart: true });
};

export const supportTicket = ticket_id => call("ticket", { ticket_id });

export const supportTicketReply = ({ ticket_id, message, files = [] }) => {
  const body = { ticket_id, message };

  if (files.length) body["files[]"] = files;

  return call("ticket-reply", body, { multipart: true });
};

/** status: 'closed' | 'in_progress' (переоткрыть) */
export const supportTicketStatus = ({ ticket_id, status }) => call("ticket-status", { ticket_id, status });

/** Скачивает вложение (бинарный поток) и отдаёт его браузеру как файл. */
export const supportDownloadAttachment = async ({ ticket_id, attachment_id, name }) => {
  const blob = await call("attachment", { ticket_id, attachment_id }, { format: "blob" });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = name || "attachment";
  document.body.appendChild(link);
  link.click();
  link.remove();

  setTimeout(() => URL.revokeObjectURL(url), 10000);
};

/** → {unread, open, last_update} */
export const supportSummary = () => call("summary");

/** Подпись приватного канала Pusher → {auth} */
export const supportRealtimeAuth = ({ socket_id, channel_name }) => call("realtime-auth", { socket_id, channel_name });
